import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Box,
  Typography,
  Alert,
  IconButton,
  Divider,
  Stack,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material';
import {
  Close as CloseIcon,
  Add as AddIcon,
  Remove as RemoveIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import config from '../config';

interface Certificate {
  id: string;
  property_address: string;
  certificate_type: string;
  certificate_type_category?: string;
  issue_date: string;
  expiry_date: string;
  normalized_address?: string;
  street_address?: string;
  flat_number?: string[] | string;
  zipcode?: string;
  inspector_name?: string;
  certificate_number?: string;
  additional_details?: string;
  notes?: string;
}

interface EditCertificateDialogProps {
  open: boolean;
  certificate: Certificate | null;
  onClose: () => void;
  onSave: (updatedCertificate: Certificate) => void;
}

const EditCertificateDialog: React.FC<EditCertificateDialogProps> = ({
  open,
  certificate,
  onClose,
  onSave
}) => {
  const { token } = useAuth();
  const [formData, setFormData] = useState<Partial<Certificate>>({});
  const [flatNumbers, setFlatNumbers] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Initialize form data when certificate changes
  useEffect(() => {
    if (certificate) {
      console.log('🔍 Certificate data received:', certificate);
      console.log('🔍 normalized_address value:', certificate.normalized_address);
      console.log('🔍 issue_date value:', certificate.issue_date);
      
      const parsedFlatNumbers = Array.isArray(certificate.flat_number) 
        ? certificate.flat_number 
        : typeof certificate.flat_number === 'string'
        ? JSON.parse(certificate.flat_number || '[]')
        : [];

      const newFormData = {
        property_address: certificate.property_address || '',
        certificate_type: certificate.certificate_type || '',
        certificate_type_category: certificate.certificate_type_category || '',
        issue_date: certificate.issue_date || '',
        expiry_date: certificate.expiry_date || '',
        normalized_address: certificate.normalized_address || '',
        street_address: certificate.street_address || '',
        zipcode: certificate.zipcode || '',
        inspector_name: certificate.inspector_name || '',
        certificate_number: certificate.certificate_number || '',
        additional_details: certificate.additional_details || '',
        notes: certificate.notes || ''
      };
      
      console.log('🔍 Form data being set:', newFormData);
      
      setFormData(newFormData);
      setFlatNumbers(parsedFlatNumbers);
    }
    setError(null);
  }, [certificate]);

  const handleInputChange = (field: keyof Certificate) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  const handleAddFlatNumber = () => {
    setFlatNumbers(prev => [...prev, '']);
  };

  const handleRemoveFlatNumber = (index: number) => {
    setFlatNumbers(prev => prev.filter((_, i) => i !== index));
  };

  const handleFlatNumberChange = (index: number, value: string) => {
    setFlatNumbers(prev => prev.map((item, i) => i === index ? value : item));
  };

  const handleSave = async () => {
    if (!certificate || !token) return;

    setLoading(true);
    setError(null);

    try {
      // Prepare data for submission
      const updateData = {
        ...formData,
        flat_number: flatNumbers.filter(fn => fn.trim() !== '') // Remove empty entries
      };

      const response = await axios.put(
        `${config.apiUrl}/api/certificates/${certificate.id}`,
        updateData,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      if (response.data.success) {
        // Create updated certificate object
        const updatedCertificate: Certificate = {
          ...certificate,
          ...formData,
          flat_number: updateData.flat_number
        };
        
        onSave(updatedCertificate);
        onClose();
      }
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to update certificate');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError(null);
    onClose();
  };

  if (!certificate) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: { minHeight: '80vh' }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6" component="div">
            Edit Certificate
          </Typography>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <Stack spacing={3}>
          {/* Certificate Information */}
          <Box>
            <Typography variant="h6" gutterBottom color="primary">
              📋 Certificate Information
            </Typography>
          </Box>

          <TextField
            fullWidth
            label="Certificate Type"
            value={formData.certificate_type || ''}
            onChange={handleInputChange('certificate_type')}
            variant="outlined"
          />

          <FormControl fullWidth variant="outlined">
            <InputLabel>Certificate Type Category</InputLabel>
            <Select
              value={formData.certificate_type_category || ''}
              onChange={(e) => setFormData(prev => ({ ...prev, certificate_type_category: e.target.value }))}
              label="Certificate Type Category"
            >
              <MenuItem value="">None</MenuItem>
              <MenuItem value="Gas Certificate">Gas Certificate</MenuItem>
              <MenuItem value="Insurance Certificate">Insurance Certificate</MenuItem>
              <MenuItem value="Licence Certificate">Licence Certificate</MenuItem>
              <MenuItem value="Electrical Certificate">Electrical Certificate</MenuItem>
              <MenuItem value="Fire Certificate">Fire Certificate</MenuItem>
              <MenuItem value="Smoke Certificate">Smoke Certificate</MenuItem>
              <MenuItem value="Deposit Protection Certificate">Deposit Protection Certificate</MenuItem>
              <MenuItem value="Energy Certificate">Energy Certificate</MenuItem>
              <MenuItem value="Other">Other</MenuItem>
            </Select>
          </FormControl>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Issue Date"
              type="date"
              value={formData.issue_date || ''}
              onChange={handleInputChange('issue_date')}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              sx={{ flex: 1 }}
            />
            <TextField
              label="Expiry Date"
              type="date"
              value={formData.expiry_date || ''}
              onChange={handleInputChange('expiry_date')}
              InputLabelProps={{ shrink: true }}
              variant="outlined"
              sx={{ flex: 1 }}
            />
          </Stack>

          {/* Address Information */}
          <Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom color="primary">
              🏠 Address Information
            </Typography>
          </Box>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Normalized Address (for tree organization)"
              value={formData.normalized_address || ''}
              onChange={handleInputChange('normalized_address')}
              helperText="This determines how properties are grouped in the tree view"
              variant="outlined"
              sx={{ flex: 2 }}
            />
            <TextField
              label="Postal Code"
              value={formData.zipcode || ''}
              onChange={handleInputChange('zipcode')}
              variant="outlined"
              sx={{ flex: 1 }}
            />
          </Stack>

          <TextField
            fullWidth
            label="Street Address"
            value={formData.street_address || ''}
            onChange={handleInputChange('street_address')}
            variant="outlined"
          />

          {/* Flat Numbers */}
          <Box>
            <Typography variant="subtitle1" gutterBottom>
              Flat Numbers
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1} mb={2}>
              {flatNumbers.map((flatNum, index) => (
                <Box key={index} display="flex" alignItems="center" gap={1}>
                  <TextField
                    size="small"
                    value={flatNum}
                    onChange={(e) => handleFlatNumberChange(index, e.target.value)}
                    placeholder={`Flat ${index + 1}`}
                    variant="outlined"
                    sx={{ minWidth: 120 }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => handleRemoveFlatNumber(index)}
                    color="error"
                  >
                    <RemoveIcon />
                  </IconButton>
                </Box>
              ))}
            </Box>
            <Button
              startIcon={<AddIcon />}
              onClick={handleAddFlatNumber}
              variant="outlined"
              size="small"
            >
              Add Flat Number
            </Button>
          </Box>

          {/* Certificate Details */}
          <Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom color="primary">
              📄 Certificate Details
            </Typography>
          </Box>

          <Stack direction="row" spacing={2}>
            <TextField
              label="Inspector Name"
              value={formData.inspector_name || ''}
              onChange={handleInputChange('inspector_name')}
              variant="outlined"
              sx={{ flex: 1 }}
            />
            <TextField
              label="Certificate Number"
              value={formData.certificate_number || ''}
              onChange={handleInputChange('certificate_number')}
              variant="outlined"
              sx={{ flex: 1 }}
            />
          </Stack>

          <TextField
            fullWidth
            label="Additional Details"
            value={formData.additional_details || ''}
            onChange={handleInputChange('additional_details')}
            multiline
            rows={2}
            variant="outlined"
          />

          {/* Notes Section */}
          <Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="h6" gutterBottom color="primary">
              📝 Notes
            </Typography>
          </Box>

          <TextField
            fullWidth
            label="Notes"
            value={formData.notes || ''}
            onChange={handleInputChange('notes')}
            multiline
            rows={4}
            placeholder="Add any additional notes about this certificate..."
            variant="outlined"
          />
        </Stack>
      </DialogContent>

      <DialogActions sx={{ p: 3 }}>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          {loading ? 'Saving...' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditCertificateDialog;